import React from "react";

import {
    Box,
    Typography,
    TextField,
    Autocomplete,
    Chip,
    CircularProgress,
    Modal,
    Card,
    CardContent,
    Button,
    Divider,
} from "@mui/material";

import { useMutation, useQueryClient } from "react-query";
import { addEventTechnician } from "../query-functions";

import { Link } from "react-router-dom";
import moment from "moment";

import { hasPermission } from "../../../../lib/components/form/RestrictedComponent";
import schema from "../../../../routes/permissions/schema";

const style = {
    margin: "auto",
    mt: {
        md: 6,
        lg: 8,
        xl: 10,
    },

    bgcolor: "background.paper",

    width: {
        xs: "100%",
        sm: "80%",
        md: "60%",
        lg: "50%",
        xl: "40%",
    },

    minHeight: "200px",
};

const EventDetails = ({
    selectedEvent,
    setSelectedEvent,
    technicians,
    clientID,
    emitSnack,
}) => {
    const queryClient = useQueryClient();
    const canEditTechnicians = hasPermission(
        schema.EVENT_TAB_TECHNICIANS.UPDATE
    );

    const {
        mutate: updateEventTechnicians,
        isLoading: addTechnicianIsLoading,
    } = useMutation(addEventTechnician, {
        onSuccess: (updatedResource) => {
            setSelectedEvent({
                ...selectedEvent,
                technicians: updatedResource.technicians,
            });

            queryClient.invalidateQueries("technicianCalendarEvents");

            emitSnack({
                type: "success",
                message: "Event Technicians Updated",
            });
        },
        onError: () => {},
    });

    return (
        <Modal
            open={selectedEvent !== null}
            onClose={() => setSelectedEvent(null)}
        >
            <Card sx={style}>
                <CardContent>
                    <Typography variant="h6">
                        Assigned Technicians{" "}
                        <Link
                            to={`/events/slim?eventID=${selectedEvent.id} `}
                            style={{
                                textDecoration: "none",
                            }}
                        >
                            {selectedEvent.title}
                        </Link>
                    </Typography>

                    <Typography variant="body1">
                        {moment(selectedEvent.start).isSame(
                            selectedEvent.end,
                            "day"
                        )
                            ? `${moment(selectedEvent.start)
                                  .format("MMM D, YYYY h:mm A")
                                  .replace(":00", "")} to ${moment(
                                  selectedEvent.end
                              )
                                  .format("h:mm A")
                                  .replace(":00", "")}`
                            : `${moment(selectedEvent.start)
                                  .format("MMM D, YYYY h:mm A")
                                  .replace(":00", "")} to ${moment(
                                  selectedEvent.end
                              )
                                  .format("MMM D, YYYY h:mm A")
                                  .replace(":00", "")}`}
                    </Typography>

                    <Autocomplete
                        multiple
                        size="small"
                        disableClearable={true}
                        sx={{ mt: 1.5 }}
                        limitTags={2}
                        value={selectedEvent.technicians}
                        readOnly={addTechnicianIsLoading || !canEditTechnicians}
                        onChange={(ev, newValue) => {
                            // takes the list of names from newValue and converts it to a list of technicianIDs from the technicians list
                            const technicianIDs = newValue.map((name) => {
                                const technician = technicians.find(
                                    (tech) => tech.techName === name
                                );
                                return technician.techID;
                            });

                            updateEventTechnicians({
                                clientID,
                                eventID: selectedEvent.id,
                                technicianIDs,
                            });
                        }}
                        options={technicians.map(
                            (technician) => technician.techName
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    variant="outlined"
                                    label={option}
                                    {...getTagProps({
                                        index,
                                    })}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="filled"
                                label="Technicians"
                                onKeyDown={(event) => {
                                    if (event.key === "Backspace") {
                                        event.stopPropagation();
                                    }
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {addTechnicianIsLoading ? (
                                                <CircularProgress
                                                    color="inherit"
                                                    size={20}
                                                />
                                            ) : (
                                                params.InputProps.endAdornment
                                            )}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </CardContent>
                <Divider />
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        p: 2,
                    }}
                >
                    <Button onClick={() => setSelectedEvent(null)}>
                        Close
                    </Button>
                </Box>
            </Card>
        </Modal>
    );
};

export default EventDetails;
